import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tab, 
  Tabs, 
  TextField, 
  MenuItem, 
  Typography, 
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  styled
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShieldIcon from '@mui/icons-material/Shield';
import StorageIcon from '@mui/icons-material/Storage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import styles from './AssetLogs.module.sass';

const SUPPORTED_CHAINS = [
  { id: 'ethereum', name: 'Ethereum' },
  { id: 'bsc', name: 'BSC' },
  { id: 'polygon', name: 'Polygon' },
  { id: 'arbitrum', name: 'Arbitrum' },
  { id: 'optimism', name: 'Optimism' }
];

// Styled Components
const StyledPaper = styled(Paper)({
  background: 'rgba(23, 23, 30, 0.95)',
  border: '1px solid rgba(115, 246, 255, 0.1)',
  borderRadius: '12px',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(115, 246, 255, 0.2)',
    boxShadow: '0 0 15px rgba(115, 246, 255, 0.1)'
  }
});

const StyledTab = styled(Tab)({
  color: 'rgba(115, 246, 255, 0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontSize: '0.7rem',
  minHeight: '48px',
  padding: '6px 16px',
  border: '1px solid rgba(115, 246, 255, 0.08)',
  borderBottom: 'none',
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: 'rgba(115, 246, 255, 0.7)',
    background: 'rgba(23, 23, 30, 0.6)'
  },
  '&.Mui-selected': {
    color: '#73F6FF',
    background: 'rgba(23, 23, 30, 0.95)',
    borderColor: 'rgba(115, 246, 255, 0.2)'
  }
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    color: '#73F6FF',
    '& fieldset': {
      borderColor: 'rgba(115, 246, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(115, 246, 255, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#73F6FF',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(115, 246, 255, 0.7)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#73F6FF',
  }
});

const StyledButton = styled(Button)({
  background: 'rgba(115, 246, 255, 0.1)',
  border: '1px solid rgba(115, 246, 255, 0.2)',
  color: '#73F6FF',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  '&:hover': {
    background: 'rgba(115, 246, 255, 0.2)',
    borderColor: 'rgba(115, 246, 255, 0.4)',
  }
});

const StyledTableCell = styled(TableCell)({
  borderBottom: '1px solid rgba(115, 246, 255, 0.1)',
  color: '#73F6FF',
  padding: '12px 16px',
  fontSize: '0.85rem',
  '&.header': {
    background: 'rgba(115, 246, 255, 0.05)',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontSize: '0.75rem',
  }
});

const StyledTableRow = styled(TableRow)({
  background: 'rgba(23, 23, 30, 0.95)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(115, 246, 255, 0.05)',
    '& .action-buttons': {
      opacity: 1,
    }
  },
  '& .action-buttons': {
    opacity: 0,
    transition: 'opacity 0.2s ease',
  }
});

const ScoreChip = styled(Box)(({ score }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 500,
  background: score >= 70 
    ? 'rgba(0, 255, 0, 0.1)' 
    : score >= 40 
      ? 'rgba(255, 165, 0, 0.1)' 
      : 'rgba(255, 0, 0, 0.1)',
  color: score >= 70 
    ? '#00ff00' 
    : score >= 40 
      ? '#ffa500' 
      : '#ff0000',
  border: `1px solid ${score >= 70 
    ? 'rgba(0, 255, 0, 0.2)' 
    : score >= 40 
      ? 'rgba(255, 165, 0, 0.2)' 
      : 'rgba(255, 0, 0, 0.2)'}`
}));

const DataTable = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress className={styles.loadingSpinner} />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell className="header">Token</StyledTableCell>
            <StyledTableCell className="header" align="right">Score</StyledTableCell>
            <StyledTableCell className="header">Token Event</StyledTableCell>
            <StyledTableCell className="header" align="right">Market Cap</StyledTableCell>
            <StyledTableCell className="header" align="right">Liquidity</StyledTableCell>
            <StyledTableCell className="header" align="right">Volume</StyledTableCell>
            <StyledTableCell className="header" align="right">Price</StyledTableCell>
            <StyledTableCell className="header" align="right">Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box component="span" className={styles.tokenIcon}>
                    {row.symbol?.charAt(0) || '?'}
                  </Box>
                  {row.name || 'Unknown Token'}
                </Box>
              </StyledTableCell>
              <StyledTableCell align="right">
                <ScoreChip score={row.score || 0}>
                  {row.score || 0}/100
                </ScoreChip>
              </StyledTableCell>
              <StyledTableCell>New Token</StyledTableCell>
              <StyledTableCell align="right">
                ${row.marketCap ? Number(row.marketCap).toLocaleString() : '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                ${row.liquidity ? Number(row.liquidity).toLocaleString() : '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                ${row.volume ? Number(row.volume).toLocaleString() : '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                ${row.price ? Number(row.price).toFixed(6) : '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                <Box className="action-buttons">
                  <IconButton size="small" sx={{ color: '#73F6FF' }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" sx={{ color: '#73F6FF' }}>
                    <ShoppingCartIcon fontSize="small" />
                  </IconButton>
                </Box>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AssetLogs = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedChain, setSelectedChain] = useState('ethereum');
  const [searchQuery, setSearchQuery] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenBankData, setTokenBankData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTokenBankData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://api.astrotek.app/api/token_bank');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setTokenBankData(data);
      } catch (error) {
        console.error('Error fetching data');
        setError('Failed to fetch token bank data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTokenBankData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index} className={styles.tabPanel}>
      {value === index && children}
    </div>
  );

  return (
    <Box className={styles.container}>
      <StyledPaper elevation={3} className={styles.paper}>
        <Box p={3}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="inherit"
            TabIndicatorProps={{
              style: {
                backgroundColor: '#73F6FF'
              }
            }}
          >
            <StyledTab 
              icon={<SearchIcon />} 
              iconPosition="start" 
              label="Blockchain Search"
              className={styles.tab}
            />
            <StyledTab 
              icon={<ShieldIcon />} 
              iconPosition="start" 
              label="Scam Detection"
              className={styles.tab}
            />
          </Tabs>

          <TabPanel value={selectedTab} index={0}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Box sx={{ width: '25%' }}>
                  <StyledTextField
                    select
                    fullWidth
                    label="Select Chain"
                    value={selectedChain}
                    onChange={(e) => setSelectedChain(e.target.value)}
                  >
                    {SUPPORTED_CHAINS.map(chain => (
                      <MenuItem key={chain.id} value={chain.id}>
                        {chain.name}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Box>
                <Box sx={{ width: '75%' }}>
                  <StyledTextField
                    fullWidth
                    label="Enter your search query..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Box>
              </Box>
          </TabPanel>

          <TabPanel value={selectedTab} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Box>
                  <StyledButton
                    startIcon={<StorageIcon />}
                    onClick={() => {/* Handle token bank data */}}
                    fullWidth
                  >
                    Use Token Bank Data
                  </StyledButton>
                </Box>
                <Box>
                  <Typography 
                    variant="body2" 
                    align="center" 
                    sx={{ color: 'rgba(115, 246, 255, 0.5)', my: 2 }}
                  >
                    or
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ width: '66.66%' }}>
                    <StyledTextField
                      fullWidth
                      label="Enter token address..."
                      value={tokenAddress}
                      onChange={(e) => setTokenAddress(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ width: '33.33%' }}>
                    <StyledTextField
                      select
                      fullWidth
                      label="Select Chain"
                      value={selectedChain}
                      onChange={(e) => setSelectedChain(e.target.value)}
                    >
                      {SUPPORTED_CHAINS.map(chain => (
                        <MenuItem key={chain.id} value={chain.id}>
                          {chain.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                  </Box>
                </Box>
              </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 2, 
                  background: 'rgba(255, 0, 0, 0.1)', 
                  color: '#ff6b6b',
                  border: '1px solid rgba(255, 0, 0, 0.2)' 
                }}
              >
                {error}
              </Alert>
            )}

            <Box mt={4}>
              <DataTable data={tokenBankData} isLoading={isLoading} />
            </Box>
          </TabPanel>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default AssetLogs;